import { Patterns } from '@common/components/forms/controls/ValidationRules';
import { VisitSalesItemAttributeListValueModel } from '@common/models/VisitSalesItemAttributeListValueModel';
import { VisitSalesItemAttributeModel } from '@common/models/VisitSalesItemAttributeModel';
import { VisitSalesItemModel } from '@common/models/VisitSalesItemModel';
import { ISalesItemAttributeListValueStorageModel } from '@common/models/requests/SalesItemAttributeListValueStorageModel';
import { ExamFormVisitBuilderAddNewLotNumber } from '@hospital/views/exam/ExamFormVisitBuilderAddNewLotNumber';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Controller, UseFormReturn, useFormContext } from 'react-hook-form';
import { SingleValue } from 'react-select';
import { FormInput, FormSelect } from '.';
import { DisplayGroup } from '../fields';
import {
  onKeyWholeNumberValidation,
  onKeyDecimalValidation,
} from '@common/utils/validationUtils';
import { maxExpirationDate } from '@common/utils';
import classNames from 'classnames';
import { ReminderDays } from '@common/models/Enums/ReminderDays';
import { SalesItemAttributeType, Traits } from '@common/models/Enums';
import { BarcodeScanner } from '@common/components/barcodeScanner';

interface IAttributeProps {
  attribute: VisitSalesItemAttributeModel;
  index: number;
}

interface IRemindersProps {
  visitSalesItem: VisitSalesItemModel;
  visitDate: DateTime | undefined;
  methods: UseFormReturn<VisitSalesItemModel, any, undefined>;
  setInvalidDueDate: (value: boolean) => void;
  disabled?: boolean;
}
interface IReminderButtonProps {
  reminderDays: ReminderDays;
  salesItemReminderDays: number;
  label: string;
  setReminderDays: (value: number) => void;
  disabled?: boolean;
}

export const DataLabelAttribute = observer(({ attribute }: IAttributeProps) => (
  <DisplayGroup text={attribute.name} value={attribute.stringValue} />
));

export const DataEntryAttribute = observer(
  ({ attribute, index }: IAttributeProps) => {
    const { control } = useFormContext<VisitSalesItemModel>();
    return (
      <FormInput
        label={attribute.name}
        type="text"
        control={control}
        fieldName={`visitSalesItemAttributes.${index}.stringValue`}
        disabled={attribute.isReadOnly}
      />
    );
  },
);

export const BarcodeEntryAttribute = observer(
  ({ attribute, index }: IAttributeProps) => {
    const { control, setValue } = useFormContext<VisitSalesItemModel>();
    const [isScanning, setIsScanning] = useState<boolean>(false);

    const decodeResult = (code: string) => {
      setValue(`visitSalesItemAttributes.${index}.stringValue`, code);
      setIsScanning(false);
    };

    return (
      <div>
        <Row>
          <Col sm={10}>
            <FormInput
              label={attribute.name}
              type="text"
              control={control}
              fieldName={`visitSalesItemAttributes.${index}.stringValue`}
              disabled={attribute.isReadOnly}
            />
          </Col>
          <Col sm={1} className="align-self-end">
            <Button
              className={`btn ${isScanning ? 'btn-danger' : 'btn-primary'} scanner-button`}
              type="button"
              onClick={() => setIsScanning(!isScanning)}
            >
              Scan
            </Button>
          </Col>
        </Row>
        {isScanning && <BarcodeScanner onDecodeResult={decodeResult} />}
      </div>
    );
  },
);

export const DataEntryNumberAttribute = observer(
  ({ attribute, index }: IAttributeProps) => {
    const { control } = useFormContext<VisitSalesItemModel>();
    return (
      <FormInput
        label={attribute.name}
        type="text" // Needs to be treated as text due to '`visitSalesItemAttributes.${index}.stringValue`'
        control={control}
        rules={{ pattern: Patterns.decimal }}
        fieldName={`visitSalesItemAttributes.${index}.stringValue`}
        disabled={attribute.isReadOnly}
        onKeyPress={(e) => onKeyDecimalValidation(e)}
      />
    );
  },
);

export const DataEntryDigitsAttribute = observer(
  ({ attribute, index }: IAttributeProps) => {
    const { control } = useFormContext<VisitSalesItemModel>();
    return (
      <FormInput
        label={attribute.name}
        type="number"
        control={control}
        rules={{ pattern: Patterns.integer }}
        onKeyPress={(e) => onKeyWholeNumberValidation(e)}
        onPaste={(e) => onKeyWholeNumberValidation(e)}
        fieldName={`visitSalesItemAttributes.${index}.stringValue`}
        disabled={attribute.isReadOnly}
        autoComplete="off"
      />
    );
  },
);

export const DateSelectorAttribute = observer(
  ({ attribute, index }: IAttributeProps) => {
    const { control } = useFormContext<VisitSalesItemModel>();
    return (
      <FormInput
        label={attribute.name}
        type="date"
        control={control}
        fieldName={`visitSalesItemAttributes.${index}.stringValue`}
        disabled={attribute.isReadOnly}
        serializationFormat="short"
        maxDate={maxExpirationDate()}
      />
    );
  },
);

export const ReminderDueDateAttribute = observer(
  ({
    visitSalesItem,
    visitDate,
    methods,
    setInvalidDueDate,
    disabled = false,
  }: IRemindersProps) => {
    const [reminderDays, setReminderDays] = useState(
      visitSalesItem.salesItemReminderDays || null,
    );
    const allowVaccines = visitSalesItem.visitSalesItemTraits.some(
      (t) => t.traitId == Traits.AllowCustomRemindersVaccines,
    );
    const isRabiesSalesItem = visitSalesItem.visitSalesItemAttributes.some(
      (vsia) => vsia.salesItemAttributeTypeId === SalesItemAttributeType.Rabies,
    );
    const localVisitDate = visitDate?.startOf('day');
    const minDate = visitDate?.plus({ days: 1 });
    const { control, watch, setValue } = useFormContext<VisitSalesItemModel>();
    const dueDate = watch('dueDate');

    useEffect(() => {
      const salesItemReminderDays = !_.isNil(dueDate)
        ? Math.trunc(
            DateTime.fromISO(dueDate).diff(localVisitDate!, 'days').days,
          )
        : null;
      if (salesItemReminderDays != reminderDays) {
        methods.setValue(`salesItemReminderDays`, salesItemReminderDays);
        setReminderDays(salesItemReminderDays);
      }
      setInvalidDueDate(_.isNil(dueDate));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dueDate]);

    useEffect(() => {
      setReminderDays(reminderDays);
      methods.setValue(`salesItemReminderDays`, reminderDays);
      if (reminderDays) {
        setValue(
          'dueDate',
          localVisitDate!.plus({ days: reminderDays! }).toISO(),
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reminderDays]);

    useEffect(() => {
      if (visitSalesItem.salesItemReminderDays !== reminderDays) {
        setReminderDays(visitSalesItem.salesItemReminderDays);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visitSalesItem.salesItemReminderDays]);

    return (
      <>
        <Col>
          <FormInput
            label="Due Date"
            portalId="root-portal"
            type="date"
            control={control}
            fieldName={`dueDate`}
            minDate={minDate?.toJSDate()}
            isRequired={allowVaccines}
            readOnly={allowVaccines && isRabiesSalesItem}
            isClearable={!isRabiesSalesItem && !disabled}
            disabled={disabled}
          />
          {!dueDate && allowVaccines && (
            <div className="invalid-feedback d-inline">Required</div>
          )}
        </Col>

        <div className="d-flex">
          {!isRabiesSalesItem && (
            <ReminderDayButton
              reminderDays={ReminderDays.Four_Weeks}
              salesItemReminderDays={reminderDays!}
              label="4 Week(s)"
              setReminderDays={setReminderDays}
              disabled={disabled} 
            />
          )}
          {!allowVaccines && !isRabiesSalesItem && (
            <ReminderDayButton
              reminderDays={ReminderDays.Three_Months}
              salesItemReminderDays={reminderDays!}
              label="3 Month(s)"
              setReminderDays={setReminderDays}
              disabled={disabled} 
            />
          )}
          {!isRabiesSalesItem && (
            <ReminderDayButton
              reminderDays={ReminderDays.Six_Months}
              salesItemReminderDays={reminderDays!}
              label="6 Month(s)"
              setReminderDays={setReminderDays}
              disabled={disabled} 
            />
          )}
          <ReminderDayButton
            reminderDays={ReminderDays.Year}
            salesItemReminderDays={reminderDays!}
            label="1 Year"
            setReminderDays={setReminderDays}
            disabled={disabled} 
          />
          {(allowVaccines || isRabiesSalesItem) && (
            <ReminderDayButton
              reminderDays={ReminderDays.Three_Years}
              salesItemReminderDays={reminderDays!}
              label="3 Year(s)"
              setReminderDays={setReminderDays}
              disabled={disabled} 
            />
          )}
        </div>
      </>
    );
  },
);

const ReminderDayButton = observer(
  ({
    reminderDays,
    salesItemReminderDays,
    label,
    setReminderDays,
    disabled = false,
  }: IReminderButtonProps) => {
    const variant =
      salesItemReminderDays == reminderDays ? 'primary' : 'secondary';
    const className =
      salesItemReminderDays === reminderDays ? '' : 'btn-outline-primary';

    const onUpdateReminderDay = () => {
      setReminderDays(reminderDays);
    };

    return (
      <Button
        variant={variant}
        className={classNames('me-2', className)}
        onClick={onUpdateReminderDay}
        disabled={disabled}
      >
        {label}
      </Button>
    );
  },
);

export const YesNoAttribute = observer(
  ({ attribute, index }: IAttributeProps) => {
    const { control, setValue } = useFormContext<VisitSalesItemModel>();
    return (
      <div className="d-flex flex-column gap-2">
        <Form.Label>{attribute.name}</Form.Label>
        {yesNo.map((opt: { label: string; value: boolean }, listValueIndex) => (
          <Controller
            key={`${attribute.name}_${listValueIndex}`}
            name={`visitSalesItemAttributes.${index}.bitValue`}
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Form.Check
                name={name}
                type="radio"
                value={opt.value.toString()}
                label={opt.label}
                disabled={attribute.isReadOnly}
                checked={value == opt.value}
                onChange={(args) => {
                  onChange(args);
                  setValue(
                    `visitSalesItemAttributes.${index}.bitValue`,
                    opt.value,
                  );
                }}
              />
            )}
          />
        ))}
      </div>
    );
  },
);

export const ListAttribute = observer(
  ({ attribute, index }: IAttributeProps) => {
    const { control, setValue } = useFormContext<VisitSalesItemModel>();
    return (
      <div className="d-flex flex-column gap-2">
        <Form.Label>{attribute.name}</Form.Label>
        {attribute.visitSalesItemAttributeListValues.map(
          (opt, listValueIndex) => (
            <Controller
              key={`${attribute.name}_${listValueIndex}`}
              name={`visitSalesItemAttributes.${index}.visitSalesItemAttributeListValues.${listValueIndex}.isSelected`}
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Form.Check
                  name={name}
                  type={attribute.allowMultiple ? 'checkbox' : 'radio'}
                  label={opt.name}
                  disabled={attribute.isReadOnly}
                  checked={value == true}
                  onChange={(args) => {
                    onChange(args);
                    if (attribute.allowMultiple) return;
                    [
                      ...Array(
                        attribute.visitSalesItemAttributeListValues.length,
                      ).keys(),
                    ].forEach((i) =>
                      setValue(
                        `visitSalesItemAttributes.${index}.visitSalesItemAttributeListValues.${i}.isSelected`,
                        i == listValueIndex,
                      ),
                    );
                  }}
                />
              )}
            />
          ),
        )}
      </div>
    );
  },
);

export const BarcodeAttribute = observer(
  ({ attribute, index }: IAttributeProps) => {
    // const { control } = useFormContext<VisitSalesItemModel>();
    return <></>;
  },
);

interface IAttributeListValueStorageModelProps extends IAttributeProps {
  attributeListValueStorageModel: ISalesItemAttributeListValueStorageModel;
  setAttributeListValueStorageModel: (
    value: ISalesItemAttributeListValueStorageModel,
  ) => void;
  setInvalidFormat: (value: boolean) => void;
  visitDate: DateTime | undefined;
  salesItemId: number;
}

export const RabiesAttribute = observer(
  ({
    attribute,
    index,
    attributeListValueStorageModel,
    setAttributeListValueStorageModel,
    setInvalidFormat,
    visitDate,
    salesItemId,
  }: IAttributeListValueStorageModelProps) => {
    const { control, setValue, getValues } =
      useFormContext<VisitSalesItemModel>();

    // The details (manufacturer, expiration, etc of the selected vaccine.
    // These will be shown back to the vet to help confirm they chose the right one.
    const initialVaccineDetails = _.find(
      attribute.visitSalesItemAttributeListValues,
      { isSelected: true },
    );
    const [vaccineDetails, setVaccineDetails] = React.useState<any>(
      initialVaccineDetails?.storageParsed,
    );
    const [isSelectedVaccineDisabled, setIsSelectedVaccineDisabled] =
      React.useState<boolean>(false);

    const onOptionChanged = (
      opt: SingleValue<VisitSalesItemAttributeListValueModel>,
    ) => {
      const updatedValues = attribute.visitSalesItemAttributeListValues.map(
        (item, i) => ({
          ...item,
          isSelected:
            item.visitSalesItemAttributeListValueId ===
            opt?.visitSalesItemAttributeListValueId,
        }),
      );

      setValue(
        `visitSalesItemAttributes.${index}.visitSalesItemAttributeListValues`,
        updatedValues,
      );
      setVaccineDetails(opt ? opt.storageParsed : undefined);
    };

    const nonExpiredRabies = attribute.visitSalesItemAttributeListValues.filter(
      (r) => {
        const expirationDate = DateTime.fromFormat(
          r.storageParsed?.expirationDate,
          'M/d/yyyy',
        );
        return expirationDate > visitDate!;
      },
    );

    const onNewLotNumberChange = (isReadOnly: boolean) => {
      attribute.isReadOnly = !isReadOnly;
      setIsSelectedVaccineDisabled(!isSelectedVaccineDisabled);
      setValue(
        `visitSalesItemAttributes.${index}.selectedRabiesVaccineId`,
        undefined,
      );
    };

    return (
      <div className="d-flex flex-column gap-2">
        <FormSelect
          control={control}
          fieldName={`visitSalesItemAttributes.${index}.selectedRabiesVaccineId`}
          label={attribute.name}
          isDisabled={attribute.isReadOnly && isSelectedVaccineDisabled}
          options={nonExpiredRabies}
          getOptionLabel={formatRabiesVaccineName}
          getOptionValue={(opt) =>
            opt.visitSalesItemAttributeListValueId.toString()
          }
          mapOptionToValue={(opt) =>
            opt?.visitSalesItemAttributeListValueId.toString()
          }
          mapValueToOption={(val) =>
            _.find(attribute.visitSalesItemAttributeListValues, {
              visitSalesItemAttributeListValueId: Number(val),
            })
          }
          onOptionChanged={onOptionChanged}
          isClearable={true}
        />
        {_.isNil(
          getValues(
            `visitSalesItemAttributes.${index}.selectedRabiesVaccineId`,
          ),
        ) ? (
          <ExamFormVisitBuilderAddNewLotNumber
            attributeListValueModel={attributeListValueStorageModel}
            setAttributeListValueModel={setAttributeListValueStorageModel}
            existingAttributeListValues={
              attribute.visitSalesItemAttributeListValues
            }
            setInvalidFormat={setInvalidFormat}
            salesItemId={salesItemId}
            onNewLotNumberChange={onNewLotNumberChange}
          />
        ) : (
          <></>
        )}
        {vaccineDetails && (
          <div
            className="d-flex flex-column gap-1"
            style={{ fontSize: 'smaller', paddingLeft: '0.5rem' }}
          >
            {vaccineDetails['vaccineName'] && (
              <div>Vaccine Name: {vaccineDetails['vaccineName']}</div>
            )}
            {vaccineDetails['serialNumber'] && (
              <div>Lot # / Serial #: {vaccineDetails['serialNumber']}</div>
            )}
            {vaccineDetails['manufacturer'] && (
              <div>Manufacturer: {vaccineDetails['manufacturer']}</div>
            )}
            {vaccineDetails['expirationDate'] && (
              <div>
                Exp Date:{' '}
                {DateTime.fromFormat(
                  vaccineDetails['expirationDate'],
                  'M/d/yyyy',
                ).toLocaleString(DateTime.DATE_SHORT)}
              </div>
            )}
            {vaccineDetails['virusType'] && (
              <div>Virus Type: {vaccineDetails['virusType']}</div>
            )}
            {vaccineDetails['administered'] && (
              <div>Administered: {vaccineDetails['administered']}</div>
            )}
          </div>
        )}
      </div>
    );
  },
);

const yesNo = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const formatRabiesVaccineName = (
  value: VisitSalesItemAttributeListValueModel,
) => {
  const expirationDate = DateTime.fromFormat(
    value.storageParsed['expirationDate'],
    'M/d/yyyy',
  );
  return `${value.name} - ${expirationDate.toLocaleString(DateTime.DATE_SHORT)} ${expirationDate < DateTime.now() ? 'Expired' : ''}`;
};
